/**
 * Module 'secrets': values stored encrypted on the hub (envelope encryption: a per-hub data
 * key wrapped by a master key) and leased to sessions Orgus launches as environment
 * variables, revoked when the session ends (architecture doc 11.7). Values are write-only:
 * no API returns them, and they never appear in state, audit, logs or events.
 */
import type { Id, Ts } from '../index.ts';
import type { PolicyScope } from './policy.ts';

/** A secret's name is the environment variable it becomes: A_Z, digits and underscores. */
export const SECRET_NAME_RE = /^[A-Z_][A-Z0-9_]{0,63}$/;
/** Values shorter than this are refused: they could not be redacted without masking ordinary text. */
export const SECRET_MIN_LENGTH = 8;
export const SECRET_MAX_LENGTH = 16_384;
/** A lease ends when its session ends, or after this long at the latest. */
export const LEASE_MAX_MS = 12 * 60 * 60_000;

/** What the console sees of a secret: never its value. */
export interface SecretMeta {
  id: Id;
  name: string;
  description?: string;
  /** org: any project may lease it; project: only that project's sessions. */
  scope: PolicyScope;
  createdBy: string;
  createdAt: Ts;
  updatedBy?: string;
  updatedAt?: Ts;
  /** Bumps each time the value is replaced. */
  valueVersion: number;
  lastLeasedAt?: Ts;
  /** Leases in the last 30 days. */
  leaseCount?: number;
  /**
   * The module that owns it ('launch-accounts', ADR 0071): it is changed only there, and leased
   * only by that module's launches, never by name.
   */
  owner?: string;
}

export type LeaseState = 'active' | 'revoked' | 'expired';

/** Secrets handed to one launched session. */
export interface SecretLease {
  id: Id;
  names: string[];
  secretIds: Id[];
  machineId: Id;
  runId: string;
  projectId: Id | null;
  sessionId: Id | null;
  createdAt: Ts;
  expiresAt: Ts;
  state: LeaseState;
  endedAt?: Ts;
  /** 'session ended', 'revoked by owner', 'expired after 12 hours'. */
  endReason?: string;
  endedBy?: string;
}

export interface SecretsKeyring {
  /**
   * env: ORGUS_SECRETS_KEY. keystore: the OS keystore (Windows Credential Manager, the macOS
   * login Keychain, the Secret Service). file: a key file in the hub folder, readable by this
   * user only, where no keystore works.
   */
  masterKeySource: 'env' | 'keystore' | 'file';
  /** With masterKeySource 'keystore': which one, "Windows Credential Manager". */
  keystore?: string;
  /** With masterKeySource 'file': why no keystore holds it, and what to do. */
  warning?: string;
  /** First 8 hex characters of the master key's SHA-256, to tell keys apart. */
  masterKeyId: string;
  dataKeyCreatedAt: Ts;
  dataKeyRotatedAt?: Ts;
  /** Set when the stored data key cannot be unwrapped (the master key changed). */
  error?: string;
}

/** state.ext.secrets */
export interface SecretsState {
  secrets: SecretMeta[];
  /** Active leases first, then the last 50 ended. */
  leases: SecretLease[];
  keyring: SecretsKeyring;
}

/** Body of POST /api/secrets and PUT /api/secrets/:id (value optional on PUT: metadata only). */
export interface SecretInput {
  name: string;
  value?: string;
  description?: string;
  scope: PolicyScope;
}

/** What other hub modules may ask the secrets module: ctx.ext<SecretsApi>('secrets'). */
export interface SecretsApi {
  /**
   * Lease these secrets to one launch. Unknown names, and project secrets of another
   * project, are reported in `missing` and not leased.
   */
  lease(o: { names: string[]; machineId: Id; runId: string; projectId: Id | null; ids?: Id[] }): {
    lease: SecretLease | null;
    env: Record<string, string>;
    missing: string[];
    /** Values of `ids` (owned secrets, ADR 0071), by secret id, leased in the same lease. */
    byId?: Record<Id, string>;
  };
  /**
   * Store a value owned by another module, or replace it (`id`), ADR 0071. Listed with its owner,
   * never leased by name, changed or deleted only through that module.
   */
  putOwned?(o: { id?: Id | null; owner: string; name: string; description: string; value: string; by: string }): SecretMeta;
  /** Delete an owned secret. */
  removeOwned?(id: Id, by: string): SecretMeta | null;
  /** The session id Claude Code reported for a launch. */
  bindSession(runId: string, sessionId: Id): void;
  /** End a launch's lease (the session ended). */
  end(runId: string, reason: string, by?: string): SecretLease | null;
  /** Values this machine was given in the last 7 days, for redaction (memory only on the agent). */
  redactionValues(machineId: Id): { name: string; value: string }[];
  /** Names of every stored secret, for the settings editor. */
  names(): string[];
}
